import React, { useEffect, useMemo, useState } from 'react';
import theme from './ProjectList.module.scss';
import { ProfileRelativeLink } from '@/components/navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { classnames } from '@/utils/html';
import ProjectTile from './ProjectTile';
import { faPlusCircle } from '@fortawesome/pro-light-svg-icons';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Project } from '@/types';
import { Trans } from '@lingui/react/macro';
import { getProjectTaskCountForProfile } from '@/services/project-dashboard';
import { useAsyncState } from '@/utils/hooks';
// import { fixCommentsWorkaround } from '@/utils/comment-workaround';
import CopyProjectModal from './CopyProjectModal';
import { copyProject } from '@/services/projects';
import { cmThreadTypes, removeIrrelevantThreads } from '@/utils/comment-workaround';
import { getCommentsOverview, getCommentsOverviewTotals } from '@/services/comments';
import { groupBy } from 'lodash-es';

interface ProjectListProps {
    projects: Project[];
    hrefGenerator?(project: Project): string;
    onClick?(project: Project): void;
    profileId: number;
    showCreateButton?: boolean;
    reloadProjects: () => void;
}

const ProjectList: React.FC<ProjectListProps> = ({
    projects,
    hrefGenerator,
    onClick,
    profileId,
    showCreateButton,
    reloadProjects,
}) => {
    const [projectTaskCountOverview] = useAsyncState(() => getProjectTaskCountForProfile(profileId), [profileId], null);
    const [projectCommentsOverview] = useAsyncState(
        () => getCommentsOverviewTotals({ projectIds: projects.map((x) => x.id), threadTypes: cmThreadTypes }),
        [projects],
        null
    );

    const projectTaskCountOverviewFixed = useMemo(() => {
        const result: { [projectId: number]: number } = {};

        projects.forEach((project) => {
            result[project.id] = 0;
        });

        projectTaskCountOverview?.forEach((item) => {
            result[item.projectId] += item.totalCount ?? 0;
        });

        projectCommentsOverview?.forEach((item) => {
            result[item.projectId] += item.unreadThreads;
        });

        return result;
    }, [projectTaskCountOverview, projectCommentsOverview]);

    const onCopyProject = async (project: Project, newProjectName: string, selectedProfileId: number) => {
        await copyProject(project.id, newProjectName, selectedProfileId);
        reloadProjects();
    };
    const [modalProject, setModalProject] = useState<Project | null>(null);
    const renderProject = (project: Project) => {
        const content = (
            <ProjectTile
                onCopyClicked={() => setModalProject(project)}
                project={project}
                numberOfTasks={projectTaskCountOverviewFixed?.[project.id]}
            />
        );

        if (hrefGenerator) {
            return (
                <ProfileRelativeLink
                    onClick={(e) => triggerHandler(onClick, project)}
                    to={hrefGenerator(project)}
                    className="link-unstyled">
                    {content}
                </ProfileRelativeLink>
            );
        }

        return (
            <div style={{ cursor: 'pointer' }} onClick={(e) => triggerHandler(onClick, project)}>
                {content}
            </div>
        );
    };
    return (
        <>
            <ul className={theme.projectList}>
                {projects.map((project) => (
                    <li key={project.id}>{renderProject(project)}</li>
                ))}
                {showCreateButton && (
                    <li>
                        <ProfileRelativeLink to={`/projects/new`} className="link-unstyled">
                            <div className={classnames(theme.project, theme.newProject)}>
                                <div>
                                    <FontAwesomeIcon icon={faPlusCircle} />
                                </div>
                                <div>
                                    <Trans>Create new project</Trans>
                                </div>
                            </div>
                        </ProfileRelativeLink>
                    </li>
                )}
            </ul>
            <CopyProjectModal
                orginProjectName={modalProject ? modalProject.name : ''}
                isOpen={modalProject !== null}
                toggle={() => setModalProject(null)}
                onSave={async (newProjectName: string, selectedProfileId: number) => {
                    if (modalProject !== null) {
                        await onCopyProject(modalProject, newProjectName, selectedProfileId);
                    }
                }}
            />
        </>
    );
};

export default ProjectList;
